import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import { db } from "../firebase";
import { toNumber } from "../utils/format";

export function subscribeByOwner(collectionName, ownerUid, onData) {
  if (!ownerUid) return () => {};

  const q = query(
    collection(db, collectionName),
    where("ownerUid", "==", ownerUid)
  );

  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data()
    }));

    onData(data);
  });
}

export function subscribeProject(projectId, onData, onMissing) {
  const ref = doc(db, "projects", projectId);

  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) {
      onMissing?.();
      return;
    }

    onData({
      id: snapshot.id,
      ...snapshot.data()
    });
  });
}

export async function saveTransaction(payload, id = null) {
  const collectionRef = collection(db, "transactions");
  const data = {
    ownerUid: payload.ownerUid,
    title: payload.title.trim(),
    category: payload.category.trim(),
    type: payload.type,
    amount: toNumber(payload.amount),
    date: payload.date,
    notes: payload.notes?.trim() || "",
    updatedAt: serverTimestamp()
  };

  if (id) {
    await updateDoc(doc(db, "transactions", id), data);
    return id;
  }

  const created = await addDoc(collectionRef, {
    ...data,
    createdAt: serverTimestamp()
  });
  return created.id;
}

export async function deleteTransaction(id) {
  await deleteDoc(doc(db, "transactions", id));
}

export async function saveAppointment(payload, id = null) {
  const collectionRef = collection(db, "appointments");
  const data = {
    ownerUid: payload.ownerUid,
    title: payload.title.trim(),
    date: payload.date,
    time: payload.time || "",
    projectName: payload.projectName?.trim() || "",
    status: payload.status || "pending",
    notes: payload.notes?.trim() || "",
    updatedAt: serverTimestamp()
  };

  if (id) {
    await updateDoc(doc(db, "appointments", id), data);
    return id;
  }

  const created = await addDoc(collectionRef, {
    ...data,
    createdAt: serverTimestamp()
  });
  return created.id;
}

export async function deleteAppointment(id) {
  await deleteDoc(doc(db, "appointments", id));
}

export async function saveProject(payload, id = null) {
  const totalValue = toNumber(payload.totalValue);
  const receivedAmount = toNumber(payload.receivedAmount);
  const data = {
    ownerUid: payload.ownerUid,
    title: payload.title.trim(),
    clientName: payload.clientName?.trim() || "",
    totalValue,
    receivedAmount,
    remainingAmount: Math.max(totalValue - receivedAmount, 0),
    startDate: payload.startDate || "",
    dueDate: payload.dueDate || "",
    status: payload.status || "active",
    description: payload.description?.trim() || "",
    updatedAt: serverTimestamp()
  };

  if (id) {
    await updateDoc(doc(db, "projects", id), data);
    return id;
  }

  const created = await addDoc(collection(db, "projects"), {
    ...data,
    createdAt: serverTimestamp()
  });
  return created.id;
}

export async function deleteProject(projectId, ownerUid, relatedTasks = [], relatedPayments = []) {
  const batch = writeBatch(db);

  batch.delete(doc(db, "projects", projectId));

  relatedTasks
    .filter((item) => item.ownerUid === ownerUid && item.projectId === projectId)
    .forEach((item) => {
      batch.delete(doc(db, "projectTasks", item.id));
    });

  relatedPayments
    .filter((item) => item.ownerUid === ownerUid && item.projectId === projectId)
    .forEach((item) => {
      batch.delete(doc(db, "projectPayments", item.id));
    });

  await batch.commit();
}

export async function addProjectTask(payload) {
  const created = await addDoc(collection(db, "projectTasks"), {
    ownerUid: payload.ownerUid,
    projectId: payload.projectId,
    text: payload.text.trim(),
    completed: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return created.id;
}

export async function toggleProjectTask(taskId, completed) {
  await updateDoc(doc(db, "projectTasks", taskId), {
    completed,
    updatedAt: serverTimestamp()
  });
}

export async function deleteProjectTask(taskId) {
  await deleteDoc(doc(db, "projectTasks", taskId));
}

export async function addProjectPayment(payload) {
  const projectRef = doc(db, "projects", payload.projectId);
  const paymentRef = doc(collection(db, "projectPayments"));
  const amount = toNumber(payload.amount);

  await runTransaction(db, async (transaction) => {
    const projectSnapshot = await transaction.get(projectRef);

    if (!projectSnapshot.exists()) {
      throw new Error("المشروع غير موجود");
    }

    const project = projectSnapshot.data();
    const nextReceived = toNumber(project.receivedAmount) + amount;
    const nextRemaining = Math.max(toNumber(project.totalValue) - nextReceived, 0);

    transaction.set(paymentRef, {
      ownerUid: payload.ownerUid,
      projectId: payload.projectId,
      amount,
      paymentDate: payload.paymentDate,
      note: payload.note?.trim() || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    transaction.update(projectRef, {
      receivedAmount: nextReceived,
      remainingAmount: nextRemaining,
      updatedAt: serverTimestamp(),
      status: nextRemaining <= 0 ? "completed" : project.status || "active"
    });
  });

  return paymentRef.id;
}

export async function deleteProjectPayment(projectId, paymentId) {
  const projectRef = doc(db, "projects", projectId);
  const paymentRef = doc(db, "projectPayments", paymentId);

  await runTransaction(db, async (transaction) => {
    const [projectSnapshot, paymentSnapshot] = await Promise.all([
      transaction.get(projectRef),
      transaction.get(paymentRef)
    ]);

    if (!projectSnapshot.exists() || !paymentSnapshot.exists()) {
      throw new Error("تعذر حذف الدفعة");
    }

    const project = projectSnapshot.data();
    const payment = paymentSnapshot.data();
    const nextReceived = Math.max(
      toNumber(project.receivedAmount) - toNumber(payment.amount),
      0
    );
    const nextRemaining = Math.max(toNumber(project.totalValue) - nextReceived, 0);

    transaction.delete(paymentRef);
    transaction.update(projectRef, {
      receivedAmount: nextReceived,
      remainingAmount: nextRemaining,
      updatedAt: serverTimestamp(),
      status: nextRemaining <= 0 ? "completed" : "active"
    });
  });
}

export async function seedUserProfile(uid, email = "") {
  await setDoc(
    doc(db, "users", uid),
    {
      uid,
      email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}
